import { useCallback, useEffect, useMemo, useState } from 'react';
import { TableCell, TableRow } from 'semantic-ui-react';
import { Circle, Desktop, Info, Printer, TrashSimple } from '@phosphor-icons/react';
import styled from 'styled-components';
import {
  ActionEditDots,
  ActionMenuItem,
  AppTable,
  ConfirmationPopup,
  DarkPopup,
} from '../../../../components';
import { WarehouseDetailHook } from '../../../../hooks';
import { ColoredPill, MuteText, ShadowBox, StyledCheckbox } from '../../../../elements';
import { useFulfillmentAddHandler } from '../../../../contexts/fulfillment-tab-actions';
import {
  CommonUtility,
  theme,
  ToastMessage,
  warehouseTooltips,
} from '../../../../utility';
import { AddPackingStations } from '../../../../page-components';
import { WarehouseService } from '../../../../utility/services/warehouse';
import { AlertFooterBar } from '../../../../elements/FooterAlert';
import { useGlobalContext } from '../../../../contexts';

const GreyCircleContainer = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${({ theme }) => theme.colors.box};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionRow = styled(TableRow)`
  position: relative;

  .action-container {
    margin: auto;
    background: transparent;
    right: 10px;
    bottom: 0;
  }

  &:hover {
    .action-container {
      opacity: 1 !important;
    }
  }
`;

function StationRow({ item, onDeleteStation }) {
  return (
    <ActionRow>
      <TableCell>
        <div className="d-flex align-items-center">
          <GreyCircleContainer>
            <Desktop color={theme.colors.grey} size={20} />
          </GreyCircleContainer>
          <span className="ml-2">{item.stationName}</span>
        </div>
      </TableCell>
      <TableCell>
        <div className="d-flex align-items-center">
          <GreyCircleContainer>
            <Printer color={theme.colors.grey} size={20} />
          </GreyCircleContainer>
          <span className="ml-2">{item.printerId}</span>
        </div>
      </TableCell>
      <TableCell>
        {item.status === 'active' ? (
          <ColoredPill color={theme.colors.green}>
            <Circle size={10} weight="fill" />
            <span>Active</span>
          </ColoredPill>
        ) : (
          <ColoredPill color={theme.colors.red}>
            <Circle size={10} weight="fill" />
            <span>Inactive</span>
          </ColoredPill>
        )}
      </TableCell>
      <TableCell>
        <ActionEditDots preventChangingBg on="hover" position="left center">
          <ActionMenuItem onClick={() => onDeleteStation(item)} className="danger">
            <TrashSimple />
            Delete
          </ActionMenuItem>
        </ActionEditDots>
      </TableCell>
    </ActionRow>
  );
}

export function PackingStationTab({ warehouseId }) {
  const {
    currentWarehouse,
    refreshWarehouseList,
    warehouseLoading: loading,
  } = useGlobalContext();

  const { data: warehouseData, loading: warehouseLoading } =
    WarehouseDetailHook(warehouseId);

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteStationPopup, setDeleteStationPopup] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [processing, setProcessing] = useState(false);
  const [shipnode, setShipnode] = useState(false);
  const [oldShipnode, setOldShipnode] = useState(false);

  const changed = useMemo(() => shipnode !== oldShipnode, [oldShipnode, shipnode]);

  const columns = [
    {
      text: 'Computer',
      key: 'computer',
      width: 2,
      textAlign: 'left',
    },
    {
      text: 'Printer',
      key: 'printer',
      textAlign: 'left',
      width: 4,
    },
    {
      text: 'Status',
      key: 'type',
      textAlign: 'left',
      width: 2,
    },
    {
      text: 'Actions',
      key: 'actions',
      textAlign: 'left',
      width: 1,
    },
  ];

  const getStationPayload = () =>
    currentWarehouse?.shipnodeStations?.map(x => ({
      stationId: x.stationId,
      stationName: x.stationName,
      shipnode: true,
      printerId: x.printerId,
      computerId: x.computerId,
      status: x.status,
    })) || [];

  const savePacking = async formData => {
    try {
      setSaving(true);

      const payload = {
        shipnodeStations: getStationPayload(),
      };

      payload.shipnodeStations.push({
        stationId: formData.computerId,
        stationName: formData.stationName,
        computerId: formData.computerId,
        printerId: formData.printerId,
      });

      await WarehouseService.updateWarehouse(warehouseId, payload);
      ToastMessage.success('Packing station added successfully');
      setShowModal(false);
      refreshWarehouseList();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  };

  const openPopup = useCallback(() => {
    setShowModal(true);
  }, []);

  useFulfillmentAddHandler(openPopup);

  const onDeleteStation = station => {
    setDeleteStationPopup(station);
  };

  const onCloseDelete = () => {
    setDeleteStationPopup(null);
  };

  const deleteStation = async () => {
    if (!deleteStationPopup) return;

    try {
      setDeleting(true);
      const payload = {
        shipnodeStations: getStationPayload().filter(
          x => x.stationId !== deleteStationPopup.stationId,
        ),
      };
      await WarehouseService.updateWarehouse(warehouseId, payload);
      ToastMessage.success('Packing station deleted successfully');
      setDeleteStationPopup(null);
      refreshWarehouseList();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setDeleting(false);
    }
  };

  const resetShipnode = defaultValue => {
    if (CommonUtility.isNotNull(defaultValue)) {
      setShipnode(defaultValue);
    } else {
      setShipnode(oldShipnode);
    }
  };

  useEffect(() => {
    if (CommonUtility.isValidObject(warehouseData)) {
      resetShipnode(warehouseData.shipnodeConfigurations?.shipnode || false);
      setOldShipnode(warehouseData.shipnodeConfigurations?.shipnode || false);
    }
  }, [warehouseData]);

  const updateShipnode = async () => {
    try {
      setProcessing(true);
      const payload = {
        shipnodeConfigurations: {
          shipnode,
        },
      };
      await WarehouseService.updateWarehouse(warehouseId, payload);
      refreshWarehouseList();
      setOldShipnode(shipnode);
      ToastMessage.success('Packing station added successfully');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <ShadowBox className="mt-4">
        <h3 className="mb-1">Printing for {currentWarehouse?.name}</h3>
        <div className="d-flex align-items-center">
          <StyledCheckbox
            checked={shipnode}
            onChange={(_, { checked }) => setShipnode(checked)}
          />
          <DarkPopup
            trigger={
              <div className="d-flex align-items-center">
                <MuteText className="ml-1">Enable ShipNode Auto-Print</MuteText>
                <Info className="ml-1 cursor-pointer" />
              </div>
            }
            position="right center"
          >
            {warehouseTooltips.shipnode}
          </DarkPopup>
        </div>
      </ShadowBox>
      <AppTable
        loading={loading}
        columns={columns}
        data={currentWarehouse?.shipnodeStations || []}
        RowComponent={<StationRow onDeleteStation={onDeleteStation} />}
      />
      <AddPackingStations
        warehouseId={warehouseId}
        savePacking={savePacking}
        saving={saving}
        existingShipnodes={currentWarehouse?.shipnodeStations || []}
        open={showModal}
        onClose={() => setShowModal(false)}
      />
      <ConfirmationPopup
        onConfirm={deleteStation}
        onClose={onCloseDelete}
        open={!!deleteStationPopup}
        loading={deleting}
        title="Delete Packing Station"
        message={`Are you sure to delete ${deleteStationPopup?.stationName}`}
      />
      {changed && !warehouseLoading && (
        <AlertFooterBar
          message="Save changes to update packing stations"
          cancel={() => resetShipnode()}
          save={updateShipnode}
          loading={processing}
        />
      )}
    </div>
  );
}
